import {nuskin, RunConfigService, events, storage, UrlService} from '@nuskin/ns-util';
import $ from '@nuskin/nuskinjquery';
import {ProductStatusMapper} from '@nuskin/ns-product-lib';
import {getCachedConfigField, getConfiguration} from '@nuskin/configuration-sdk';

/**
 * Storage metadata for caching product statuses in session storage.
 * @type {* | {key, type, storageType}}
 * @memberof ProductStatusService
 * @private
 */
const STORAGE_META = storage.metadata.PRODUCT_STATUS;

/**
 * Time-to-live (sec) for cached statuses with a highavailable quantity threshold.
 * @type {number | null}
 * @param {String} [countryCode] - optional country code; defaults to current country
 * @memberof ProductStatusService
 * @private
 */
let ttlHigh = null;
const getTtlHigh = (countryCode) => valueOrFromConfig(ttlHigh, 'productStatusTtlHigh', 300, countryCode);

/**
 * Time-to-live (sec) for cached statuses with a medium available quantity threshold.
 * @type {number | null}
 * @param {String} [countryCode] - optional country code; defaults to current country
 * @memberof ProductStatusService
 * @private
 */
let ttlMedium = null;
const getTtlMed = (countryCode) => valueOrFromConfig(ttlMedium, 'productStatusTtlMed', 120, countryCode);

/**
 * Time-to-live (sec) for cached statuses with a low available quantity threshold.
 * Statuses with a lower available quantity than this are not cached.
 * @type {number | null}
 * @param {String} [countryCode] - optional country code; defaults to current country
 * @memberof ProductStatusService
 * @private
 */
let ttlLow = null;
const getTtlLow = (countryCode) => valueOrFromConfig(ttlLow, 'productStatusTtlLow', 30, countryCode);

/**
 * Threshold to cache with a high available quantity.
 * This is used by the Time-to-live high value.
 * @type {number | null}
 * @param {String} [countryCode] - optional country code; defaults to current country
 * @memberof ProductStatusService
 * @private
 */
let highThreshold = null;
const getHighThreshold = (countryCode) => valueOrFromConfig(highThreshold, 'productStatusQtyThreshHigh', 1000, countryCode);

/**
 * Threshold to cache with a medium available quantity.
 * This is used by the Time-to-live medium value.
 * @type {number | null}
 * @param {String} [countryCode] - optional country code; defaults to current country
 * @memberof ProductStatusService
 * @private
 */
let medThreshold = null;
const getMedThreshold = (countryCode) => valueOrFromConfig(medThreshold, 'productStatusQtyThreshMed', 500, countryCode);

/**
 * Threshold to cache with a low available quantity.
 * This is used by the Time-to-live low value.
 * @type {number | null}
 * @param {String} [countryCode] - optional country code; defaults to current country
 * @memberof ProductStatusService
 * @private
 */
let lowThreshold = null;
const getLowThreshold = (countryCode) => valueOrFromConfig(lowThreshold, 'productStatusQtyThreshLow', 100, countryCode);

/**
 * Maximum wait time (ms) between starting a new batch and executing its ajax
 * request.
 * @type {number | null}
 * @param {String} [countryCode] - optional country code; defaults to current country
 * @memberof ProductStatusService
 * @private
 */
let ajaxBatchMaxWaitTime = null;
const getAjaxBatchMaxWaitTime = (countryCode) => valueOrFromConfig(ajaxBatchMaxWaitTime, 'productStatusBatchWaitTime', 500, countryCode);

/**
 * Maximum number of SKUs that can be in one batch status request.
 * This limit is set to avoid sending requests too large for the server to
 * process.
 * @type {number | null}
 * @param {String} [countryCode] - optional country code; defaults to current country
 * @memberof ProductStatusService
 * @private
 */
let ajaxBatchMaxSize = null;
const getAjaxBatchMaxSize = (countryCode) => valueOrFromConfig(ajaxBatchMaxSize, 'productStatusRequestMaxSize', 36, countryCode);

/**
 * Wait time (ms) between starting a new subscriber batch and processing it
 * for publishing. Publishing itself does not actually occur until all
 * subscribers' status requests have been satisfied.
 * @type {number | null}
 * @param {String} [countryCode] - optional country code; defaults to current country
 * @memberof ProductStatusService
 * @private
 */
let subBatchWaitTime = null;
const getSubBatchWaitTime = (countryCode) => valueOrFromConfig(subBatchWaitTime, 'productStatusPublishWaitTime', 250, countryCode);

/**
 * Product status request url. This is set at runtime based on market
 * configuration.
 * @type {string | null}
 * @memberof ProductStatusService
 * @private
 */
let productStatusUrl = null;

/**
 * Object representing the current batch of SKUs waiting for a status request
 * to be made. As getProductStatus() is called, SKUs are added to this object
 * until either its maximum wait time is reached or its maximum batch size is
 * reached, at which point an ajax request is made and the current batch is set
 * to null to await the next SKU needing an ajax request.
 * @type {*}
 * @memberof ProductStatusService
 * @private
 */
let currentAjaxBatch;

/**
 * Object representing the current batch of SKUs that are subscribed to this
 * service's published status data events. Null if there are no unsatisfied
 * subscriber requests.
 * @memberof ProductStatusService
 * @private
 */
let currentSubscriberBatch;

/*
Product Status Service subscribes to the ADD_SKU_PRODUCT_STATUS_QUEUE event to
allow external code to request product status data through event publishing.
 */
events.subscribe(events.shop.ADD_SKU_PRODUCT_STATUS_QUEUE, queueForPublish);

/**
 * The Product Status Service provides methods for retrieving product status
 * data. This data is cached locally to reduce the server load where possible.
 *
 * ### CALLING THE SERVICE DIRECTLY
 * A requester can call the service directly to receive product statuses of
 * one or more SKUs.
 *
 * Example request for a single SKU:
 * <pre>
 * ProductStatusService.getProductStatus('12345678')
 *     .then(statusList => status12345678 = statusList[0]);
 * </pre>
 *
 * Example request for multiple SKUs:
 * <pre>
 * ProductStatusService.getProductStatus(['12345678', '99999999])
 *     .then(statusList => statusList.forEach(doStuff));
 * </pre>
 *
 *
 * ### CALLING THE SERVICE THROUGH PUB-SUB EVENTS
 * A requester can use a pub-sub model to request data from this service.
 * Publish to `events.shop.ADD_SKU_PRODUCT_STATUS_QUEUE` with the SKU or array
 * of SKUs you wish to request data for.
 * Subscribe to `events.shop.PRODUCT_STATUS_RESULT` to receive the requested data.
 *
 * Example:
 * <pre>
 * let mySKU = '12345678';
 *
 * events.subscribe(events.shop.PRODUCT_STATUS_RESULT, statuses => {
 *     myStatus = statuses.find(stat => stat.SKU === mySKU);
 * });
 *
 * events.publish(events.shop.ADD_SKU_PRODUCT_STATUS_QUEUE, mySKU);
 * </pre>
 *
 * #### NOTE:
 * Subscribers to `events.shop.PRODUCT_STATUS_RESULT` receive the combined
 * statuses requested by all subscribers, and will need to filter it to obtain
 * their own requested data.
 *
 * It is recommended to use direct calls to the Product Status Service
 * where feasible.
 *
 * @namespace ProductStatusService
 */
export default {
    getProductStatus,
    processStatusQueue,
    reset,
    addSKUToQueue
};


//=================================================================//
//
//  PUBLIC FUNCTIONS
//
//=================================================================//

/**
 * Retrieves product status data for one or more product SKUs.
 * @param {Array<String> | String} skus - single SKU string or Array of SKU strings
 * @param {String} [countryCode] - optional country code; defaults to current country
 * @param {{filter?: string, timeout?: number, version?: number}} [options] - additional options
 *          (filter to use, timeout in milliseconds, version as an integer (1 or 2))
 * @param {string} [options.filter] - filter string
 * @param {number} [options.timeout] - request timeout, in milliseconds
 * @return {Promise<Array<ProductStatus>>} Promise that resolves to an Array of product status objects
 * @memberof ProductStatusService
 * @public
 */
async function getProductStatus(skus, countryCode, queryParams = {}) {
    const options = {countryCode: countryCode|| RunConfigService.getRunConfig().country};
    if (queryParams.version) {
        const version = queryParams.version;
        if ([1,2].includes(version)){
            options.version = queryParams.version;
        } else {
            return Promise.reject(`Illegal Argument Error: version may only be "1" or "2". Received "${version}".`);
        }
    }

    await getConfiguration(['Shopping', 'Url'])
    queryParams.context = getCachedConfigField('productStatusContext');
    queryParams.storageLocation = getCachedConfigField('productStatusStorageLocation');
    options.filter = UrlService.toQueryString(queryParams);

    // Check data type of skus argument
    if ((typeof skus !== 'string') && !(skus instanceof Array)) {
        let type = (skus === null) ? 'null' :
            (skus === undefined) ? 'undefined' :
                (typeof skus === 'object') ? skus.constructor.name :
                    (typeof skus);
        return Promise.reject(`Illegal Argument Error: ProductStatusService expected string or Array, got ${type}`);
    }

    // If single SKU string, transform to array
    if (typeof skus === 'string') skus = [skus];

    // Test SKUs in array for validity before proceeding further
    if (!skus.every(isValidSKU)) {
        return Promise.reject('ProductStatusService Error: request contains one or more invalid SKUs');
    }

    let promises = skus.map((sku) => {
        // to avoid caching issues with agelocme products, don't get from cache
        if (queryParams.agelocme) {
            return queueForAjaxRequest(sku, options);
        }

        let cachedStatus = getStatusFromCache(sku, options.countryCode);
        if (cachedStatus) {
            return Promise.resolve(cachedStatus);
        } else {
            return queueForAjaxRequest(sku, options);
        }
    });

    return Promise.all(promises)
        .then(results => results.filter(el => el));
}

/**
 * Calling this function forces early processing of the current batch of SKUs
 * awaiting an ajax request for status data. This is not generally recommended,
 * as forcing early batch processing may result in a greater number of ajax
 * requests.
 * @memberof ProductStatusService
 * @public
 */
function processStatusQueue() {
    let batch = getCurrentAjaxBatch();
    clearTimeout(batch.timeoutId);
    processAjaxBatch();
}

/**
 * Resets this service's internal state. This method is intended for use in
 * testing and is not recommended under normal use conditions.
 * @memberof ProductStatusService
 * @public
 */
function reset() {
    ttlHigh = null;
    ttlMedium = null;
    ttlLow = null;
    ajaxBatchMaxWaitTime = null;
    ajaxBatchMaxSize = null;
    subBatchWaitTime = null;
    productStatusUrl = null;
    currentAjaxBatch = null;
    currentSubscriberBatch = null;
}

/**
 * Add a SKU to the product status queue to be processed in the batch request
 * This is to replace the subscribe feature that was used previously.
 * @param {Array<String> | String} skus - single SKU string or Array of SKU strings
 * @memberof ProductStatusService
 * @public
 */
function addSKUToQueue(skus) {
    // Check data type of skus argument
    if ((typeof skus !== 'string') && !(skus instanceof Array)) {
        let type = (skus === null) ? 'null' :
            (skus === undefined) ? 'undefined' :
                (typeof skus === 'object') ? skus.constructor.name :
                    (typeof skus);
        console.log(`Illegal Argument Error: addSKUToQueue expected string or Array, got ${type}`);
    }

    // If single SKU string, transform to array
    if (typeof skus === 'string') skus = [skus];

    skus.forEach(function (sku) {
        queueForPublish(sku);
    });
}

//=================================================================//
//
//  PRIVATE FUNCTIONS
//
//=================================================================//

/**
 * Queues the given SKU(s) for publishing once their status requests are
 * satisfied.
 * @param {Array<String> | String} sku - single SKU string or Array of SKU strings
 * @memberof ProductStatusService
 * @private
 */
function queueForPublish(sku) {
    // Test the given SKU for validity; do not enqueue if invalid
    if (!isValidSKU(sku)) {
        console.error('ProductStatusService Error: invalid SKU', sku);
        return;
    }

    getConfiguration(['Url', 'Shopping']).then(() => {
        let batch = getCurrentSubscriberBatch();
        batch.requests.push(getProductStatus(sku)
            .catch(err => {console.error(err); return null;}));
    });
}

/**
 * Returns the current pending batch of subscribers.
 * @returns {Object} current subscriber batch object
 * @memberof ProductStatusService
 * @private
 */
function getCurrentSubscriberBatch() {
    if (!currentSubscriberBatch) {
        currentSubscriberBatch = {
            requests: []
        };

        currentSubscriberBatch.timeoutId = setTimeout(publishStatuses, getSubBatchWaitTime());
    }

    return currentSubscriberBatch;
}

/**
 * Processes the current batch of subscribers. Waits for all subscribers'
 * status requests to be satisfied, and then publishes a Product Status
 * Result event containing all requested data.
 * @memberof ProductStatusService
 * @private
 */
function publishStatuses() {
    if (currentSubscriberBatch &&
        currentSubscriberBatch.requests.length > 0) {

        // Reset the subscriber batch
        let batch = currentSubscriberBatch;
        currentSubscriberBatch = null;

        // Wait for all status requests to be satisfied
        Promise.all(batch.requests)

        // Filter out nulls and flatten the status lists to a single list
            .then(lists => lists.filter(s => !!s).reduce((acc, el) => acc.concat(el), []))

            // Publish the flattened list to all subscribers
            .then(statuses => events.publish(events.shop.PRODUCT_STATUS_RESULT, [statuses]))
            .catch(console.error);
    }
}

/**
 * Adds the given SKU to the current pending request batch.
 * @param {String} sku
 * @param {Object} [options] - additional options
 * @memberof ProductStatusService
 * @private
 */
function queueForAjaxRequest(sku, options) {
    let batch = getCurrentAjaxBatch(options);

    batch.skus.add(sku);

    if (batch.skus.size >= getAjaxBatchMaxSize(options.countryCode)) {
        clearTimeout(batch.timeoutId);
        processAjaxBatch();
    }

    return batch.promise.then(stats => stats.find(el => el.SKU === sku));
}

/**
 * Returns the current pending request batch.
 * If no batch is currently pending, a new one is created.
 * @param {Object} [options] - additional options
 * @returns {Object} Current request batch object
 * @memberof ProductStatusService
 * @private
 */
function getCurrentAjaxBatch(options) {
    if (!currentAjaxBatch) {
        let callback = () => {};

        let promise = new Promise((resolve, reject) => {
            // Promise resolution is tied to a callback function to allow this
            // promise to be resolved by an external function call.
            callback = (err, data) => {
                if (err) reject(err);
                else resolve(data);
            };
        });

        currentAjaxBatch = {
            options,
            skus: new Set(),
            promise,
            callback
        };

        // Timeout ID is attached to the batch object to allow the timeout to
        // be cancelled if the batch needs to be processed early.
        currentAjaxBatch.timeoutId = setTimeout(processAjaxBatch, getAjaxBatchMaxWaitTime(options.countryCode));
    }

    return currentAjaxBatch;
}

/**
 * Processes the current batch of SKUs. Processing consists of making an ajax
 * call to the product status REST endpoint and then calling the batch's
 * callback function with the returned data in order to resolve its promise to
 * this service's callers.
 * @memberof ProductStatusService
 * @private
 */
function processAjaxBatch() {
    if (currentAjaxBatch && currentAjaxBatch.skus.size > 0) {
        let batch = currentAjaxBatch;
        currentAjaxBatch = null;

        let skus = [...batch.skus];
        fetchProductStatuses(skus, batch.options)
            .then(statuses => batch.callback(null, statuses))
            .catch(err => batch.callback(err));
    }
}

/**
 * Validates the response object based on the version number provided.
 * @param {reject} reject - A callback to reject the transform if needed.
 * @param {Object} response - An integer indicating the version number to validate against.
 * @returns {Object} The response transformed to V1.
 * @memberof ProductStatusService
 * @private
 */
function transformV2ResponseToV1(reject, response) {
    response.productStatus = [];
    if (response.items) {
        response.productStatus = ProductStatusMapper.createProductStatusesFromV2(response);
        for(const productStatus of response.productStatus) {
            if(!productStatus.SKU && !!productStatus.sku) {
                // maintain the case-sensitive "SKU" prop for backwards compatibility with ns-shop usages
                productStatus.SKU = productStatus.sku;
            }
        }
    } else {
        console.error('Server-side product status service returned and error: ', response);
        reject(response);
    }
    return response;
}

/**
 * Validates the response object.
 * @param {Object} response - The response object to validate.
 * @memberof ProductStatusService
 * @private
 */
function isResponseValid(response) {
    const statusExists = response.hasOwnProperty('status');
    const itemsExists = response.hasOwnProperty('items');
    const responseOK = statusExists && Number.parseInt(response['status']) === 200;
    // If the response is OK (200) and items exists, the response is valid.
    return responseOK && itemsExists;
}

/**
 * Executes an ajax request against the product status url for the given SKUs.
 * @param {Array<String>} skus
 * @param {Object} [options] - additional options:
 * @param {String} [options.countryCode] - country code; default to current country
 * @param {String} [options.filter] - filter query string
 * @param {Number} [options.timeout] - ajax request timeout; defaults to 10s
 * @param {Number} [options.version] - the response version to return (integer). e.g. "1", "2" ...
 * @returns {Promise<Array<ProductStatus>>}
 * @memberof ProductStatusService
 * @private
 */
function fetchProductStatuses(skus, options) {
    return fetchProductStatusesV2(skus, options);
}

function fetchProductStatusesV2(skus, options) {
    const DEFAULT_OPTIONS = {
        timeout: 10000,
        filter: '',
        countryCode: RunConfigService.getRunConfig().country,
        version: 1
    };
    options = Object.assign({}, DEFAULT_OPTIONS, options);

    let data = {};
    const requestedQuantity = getRequestedQuantity();
    if (skus) {
        skus.forEach(sku => {
            data[sku] = {
                requestedQuantity
            };
        });
    }

    let headers = {
        client_id: nuskin.configuration.checkout.clientId,
        client_secret: nuskin.configuration.checkout.clientSecret
    };

    let url = (options.countryCode ? getProductStatusUrl(options.countryCode) : getProductStatusUrl());
    if (options.filter) {
        url = url + (url.indexOf('?') ? '&' : '?') + `${options.filter}`;
    }

    const getAjaxOptions = (resolve, reject, transform) => ({
        type: 'POST',
        url,
        timeout: options.timeout,
        contentType: 'application/json',
        headers,
        data: JSON.stringify(data),
        success: function(response) {
            if (isResponseValid(response)) {
                if (transform) {
                    transform(reject, response);
                }

                const responseIndex = options.version === 2 ? 'items' : 'productStatus';
                let statuses = checkMaxQuantities(response[responseIndex]);
                saveStatusesToCache(statuses, options.countryCode);
                resolve(statuses);
            } else {
                console.error('Server-side product status service returned an error: ', response);
                reject(response);
            }
        },
        error: function(response) {
            console.error('Server-side product status service returned an error: ', response);
            reject(response);
        }
    });

    return new Promise((resolve, reject) => {
        if (options.version === 2) {
            $.ajax(getAjaxOptions(resolve, reject));
        } else {
            $.ajax(getAjaxOptions(resolve, reject, transformV2ResponseToV1));
        }
    });
}

function checkMaxQuantities(_statuses) {
    let statuses = _statuses,
        configMaxes = getCachedConfigField('skuMaxQty') || [];

    try {
        configMaxes = configMaxes
            .map((cm) => JSON.parse(cm))
            .reduce((map, cmItem) => {
                map[cmItem.sku] = cmItem.maxQty;
                return map;
            }, {});
    } catch (err) {
        console.log('Error parsing skuMaxQty from config', err);
        configMaxes = {};
    }
    statuses.forEach(status => {
        status.maxQuantity = status.availableQuantity < 999 ? status.availableQuantity : 999;
    });
    if (configMaxes && Object.keys(configMaxes).length > 0) {
        statuses.forEach(status => {
            if (configMaxes.hasOwnProperty(status.SKU)) {
                if (configMaxes[status.SKU] < status.availableQuantity) {
                    status.maxQuantity = configMaxes[status.SKU];
                }
            }
        });
    }

    return statuses;
}

/**
 * Writes the list of product statuses to local cache (session storage).
 * @param {Array<Object>} productStatuses
 * @param {String} country
 * @memberof ProductStatusService
 * @private
 */
function saveStatusesToCache(productStatuses, country) {
    if (productStatuses) {
        productStatuses.forEach(status => {
            let ttl = status.availableQuantity > getHighThreshold(country) ? getTtlHigh(country) :
                status.availableQuantity > getMedThreshold(country) ? getTtlMed(country) :
                    status.availableQuantity > getLowThreshold(country) ? getTtlLow(country) :
                        null;

            if (ttl !== null) {
                storage.setItem(getStorageMeta(status.SKU, country), {status: status}, {ttl: ttl * 1000});
            }
        });
    }
}

/**
 * Returns the cached status object corresponding to the given SKU.
 * Returns null if no cached status object can be found.
 * @param {String} sku
 * @param {String} country
 * @returns {ProductStatus | null}
 * @memberof ProductStatusService
 * @private
 */
function getStatusFromCache(sku, country) {
    let cachedItem = storage.getItem(getStorageMeta(sku, country));
    return cachedItem ? cachedItem.status : null;
}

/**
 * Returns the requested quantity, or 10000 if an error occurs.
 * @returns number requestedQuantity
 * @memberof ProductStatusService
 * @private
 */
function getRequestedQuantity() {
    let requestedQuantity = 10000;

    try {
        requestedQuantity = parseInt(getCachedConfigField('productStatusRequestedQuantity').match(/^\d+$/)[0]);
    } catch (err) {
        console.error(`Bad or no requested quantity for the product status call: ${getCachedConfigField('productStatusRequestedQuantity')}`);
    }
    return requestedQuantity;
}

/**
 * Returns the url of the product status REST endpoint, based on current
 * configuration.
 * @param {String} [country] - Used to pull the productStatusUrl from a country other than the current country
 * @returns {string}
 * @memberof ProductStatusService
 * @private
 */
function getProductStatusUrl(country){
    return getProductStatusUrlV2(country);
}

function getProductStatusUrlV2(country = '') {
    if (!productStatusUrl || typeof(country) === 'string'){
        productStatusUrl = `${UrlService.getAwsUrl()}/product/v2/status?country=${country}`;
    }
    return productStatusUrl;
}

/**
 * Returns a storage util metadata object keyed to the given SKU and country code.
 * @param {String} sku
 * @param {String} country
 * @returns {{key: string, type: string, storageType: string}}
 * @memberof ProductStatusService
 * @private
 */
function getStorageMeta(sku, country) {
    return Object.assign({}, STORAGE_META, {key: `${STORAGE_META.key}-${sku}-${country}`});
}

/**
 * Returns the given value if it is defined and not null. Falls back first to
 * retrieving the value from market config using the given key. If the value
 * cannot be retrieved from market config, falls back to the given fallback
 * value.
 * @param {*} value
 * @param {String} configKey
 * @param {*} fallback
 * @returns {*}
 * @memberof ProductStatusService
 * @private
 */
function valueOrFromConfig(value, configKey, fallback) {
    if (value === undefined || value === null) {
        return getCachedConfigField(configKey) || fallback;
    }
    return value;
}

/**
 * Tests the given SKU for correct data type and pattern.
 * @param {*} sku
 * @returns {boolean}
 * @memberof ProductStatusService
 * @private
 */
function isValidSKU(sku) {
    return (typeof sku === 'string') && sku !== '';
}

/**
 * Product status information object.
 * @typedef ProductStatus
 * @property {string} SKU - Product SKU.
 * @property {number} availableQuantity - Product available quantity.
 * @property {string} availableQuantityStatus - Status of the available quantity: "ESTIMATED", "CURRENT", "STALE", "UNKNOWN".
 * @property {string[]} orderType - Order types that may include this product, e.g. "ORDER", "ADR"
 * @property {string} status - Status of this product: "RELEASED_FOR_SALE", "DISCONTINUED", "PHASE_OUT", "APPROVED_FOR_SALE".
 * @property {boolean} [webEnabled] - Flag indicating whether this product is enabled for sale on the web.
 * @property {Object<string, number>} price - Price map for this product, keyed by price type: "RTL", "WHL", "WADR", "WADW", "WRTL", etc.
 * @property {Object<string, number>} psv - PSV map for this product, keyed by price type: "WHL", "WWHL", "WADW", etc.
 * @property {Object<string, number} csv - CSV map for this product, keyed by price type: "WHL", "WWHL", "WADW", etc.
 */
